<template>
  <Transition name="booking-sticky">
    <div
      v-if="visible"
      class="booking-sticky"
      role="region"
      aria-label="Schnellbuchung"
    >
      <button
        type="button"
        class="booking-sticky__button"
        @click="onStickyClick"
      >
        Jetzt reservieren
      </button>
    </div>
  </Transition>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useBookingModal } from '../../composables/useBookingModal.js'
import { trackCtaClick } from '../../lib/analytixTracking.js'

const MOBILE_MAX_WIDTH = 767
const BOOKING_ZONE_IDS = ['pricing', 'ratings', 'gallery', 'impressions', 'notices']

export default {
  setup() {
    const { openBookingModal, bookingModalOpen } = useBookingModal()
    const visible = ref(false)

    const onStickyClick = () => {
      trackCtaClick('Jetzt reservieren', { placement: 'sticky' })
      openBookingModal()
    }

    let heroObserver = null
    let apartmentsObserver = null
    let contactObserver = null
    let bookingZoneObserver = null
    let mediaQuery = null
    let showTimer = null

    let isMobile = false
    let heroVisible = true
    let apartmentsVisible = false
    let contactVisible = false
    let bookingZoneActive = false

    const computeTargetVisible = () =>
      isMobile &&
      bookingZoneActive &&
      !heroVisible &&
      !apartmentsVisible &&
      !contactVisible &&
      !bookingModalOpen.value

    const updateVisibility = (immediate = false) => {
      const shouldShow = computeTargetVisible()

      if (showTimer) {
        clearTimeout(showTimer)
        showTimer = null
      }

      if (!shouldShow) {
        visible.value = false
        return
      }

      if (immediate || visible.value) {
        visible.value = true
        return
      }

      showTimer = setTimeout(() => {
        if (computeTargetVisible()) {
          visible.value = true
        }
      }, 180)
    }

    const updateIsMobile = () => {
      if (typeof window === 'undefined') return
      isMobile = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches
      updateVisibility(true)
    }

    watch(bookingModalOpen, () => {
      updateVisibility(true)
    })

    watch(visible, (isVisible) => {
      document.body.classList.toggle('booking-sticky-active', isVisible)
    })

    onMounted(() => {
      updateIsMobile()

      mediaQuery = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', updateIsMobile)
      } else {
        mediaQuery.addListener(updateIsMobile)
      }

      const hero = document.getElementById('home')
      const apartments = document.getElementById('apartments')
      const contact = document.getElementById('contact_us')

      if (hero && 'IntersectionObserver' in window) {
        heroObserver = new IntersectionObserver(
          ([entry]) => {
            heroVisible = entry.isIntersecting && entry.intersectionRatio > 0.12
            updateVisibility()
          },
          { threshold: [0, 0.12, 0.35], rootMargin: '-72px 0px 0px 0px' }
        )
        heroObserver.observe(hero)
      } else {
        heroVisible = false
      }

      if (apartments && 'IntersectionObserver' in window) {
        apartmentsObserver = new IntersectionObserver(
          ([entry]) => {
            apartmentsVisible = entry.isIntersecting && entry.intersectionRatio > 0.2
            updateVisibility()
          },
          { threshold: [0, 0.2, 0.45], rootMargin: '-72px 0px -15% 0px' }
        )
        apartmentsObserver.observe(apartments)
      }

      if (contact && 'IntersectionObserver' in window) {
        contactObserver = new IntersectionObserver(
          ([entry]) => {
            contactVisible = entry.isIntersecting
            updateVisibility(true)
          },
          { threshold: 0, rootMargin: '0px 0px -25% 0px' }
        )
        contactObserver.observe(contact)
      }

      if ('IntersectionObserver' in window) {
        const visibleZones = new Set()

        bookingZoneObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const id = entry.target.id
              if (entry.isIntersecting) {
                visibleZones.add(id)
              } else {
                visibleZones.delete(id)
              }
            })
            bookingZoneActive = visibleZones.size > 0
            updateVisibility()
          },
          { threshold: 0.08, rootMargin: '-72px 0px -20% 0px' }
        )

        BOOKING_ZONE_IDS.forEach((id) => {
          const element = document.getElementById(id)
          if (element) {
            bookingZoneObserver.observe(element)
          }
        })
      }
    })

    onBeforeUnmount(() => {
      if (showTimer) clearTimeout(showTimer)
      document.body.classList.remove('booking-sticky-active')
      heroObserver?.disconnect()
      apartmentsObserver?.disconnect()
      contactObserver?.disconnect()
      bookingZoneObserver?.disconnect()
      if (!mediaQuery) return
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateIsMobile)
      } else {
        mediaQuery.removeListener(updateIsMobile)
      }
    })

    return {
      visible,
      onStickyClick,
    }
  }
}
</script>

<style scoped lang="scss">
.booking-sticky {
  display: none;
}

@media (max-width: 767px) {
  .booking-sticky {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    padding: 0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom, 0px));
    background: rgba(255, 255, 255, 0.96);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(8px);
  }

  .booking-sticky__button {
    display: block;
    width: 100%;
    padding: 0.9rem 1.25rem;
    font-size: 1rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    border: 2px solid #f2c900;
    border-radius: 4px;
    background: #f2c900;
    color: #515a5f;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(242, 201, 0, 0.35);
  }
}

.booking-sticky-enter-active,
.booking-sticky-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.booking-sticky-enter-from,
.booking-sticky-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

:global(body.booking-sticky-active) {
  @media (max-width: 767px) {
    padding-bottom: calc(4.5rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>
